/*
  viral-withdrawal.js  (sections/viral-withdrawal.liquid)

  Progressive enhancement of the online withdrawal function (Directive 2011/83/EU art. 11a). The form works without
  this file: both steps are then on one screen, step 2 being a required checkbox plus the confirm button.

  What the script adds
  1. Two real screens. Step 1 = the statement form, step 2 = a review of what will be sent plus the statutory
     confirm button. While step 1 is showing, the confirm button (the form's only submit button) is DISABLED, so
     pressing Enter in a field cannot submit the statement by accident: Enter moves on to the review instead.
  2. Accessible validation before the review: error summary with role="alert" that receives focus and links to each
     field, aria-invalid + aria-describedby on the fields, messages taken from data-error-message (locale files).
  3. A courtesy copy on the confirmation screen: what was sent and when (device clock, with time zone), so the
     customer can print or save it. It is NOT the acknowledgement of receipt the directive asks for: that one is an
     e-mail the merchant sends (docs/SETUP_SHOPIFY.md section 8).
  4. Field restore after a server-side error (Shopify only gives back the e-mail field).

  What it never does
  - It never submits the form itself and never cancels the customer's submit on step 2. The POST is native, because
    Shopify's bot protection hooks the real submit event and may redirect to /challenge (hCaptcha). A fetch() or a
    form.submit() call would skip it.
  - No user-facing text lives here: every string comes from the section (data-* attributes or rendered nodes).

  Storage: one sessionStorage entry, VIRAL_WITHDRAWAL_KEY. Written when the customer reaches step 2, flagged
  `pending` when they press the confirm button, deleted as soon as the confirmation screen has shown it. The flag is
  dropped whenever the form screen loads again, so an abandoned attempt can never be displayed as "received".
  Per tab, never sent anywhere, every access wrapped in try/catch because storage can be blocked.

  Address bar: Shopify shows the success state for any URL carrying ?contact_posted=true. Once the confirmation is
  on screen the parameter is removed (history.replaceState), so reloading or reopening the address from the browser
  history shows the form again instead of a confirmation for something that was not sent.
*/

if (!customElements.get('viral-withdrawal')) {
  // declared inside the guard so a second copy of this script tag cannot throw
  const VIRAL_WITHDRAWAL_KEY = 'viral-withdrawal:statement';
  const VIRAL_WITHDRAWAL_FIELDS = ['name', 'order', 'email', 'items', 'reason'];
  const VIRAL_WITHDRAWAL_RESUBMIT_MS = 4000;

  const readDraft = () => {
    try {
      const raw = window.sessionStorage.getItem(VIRAL_WITHDRAWAL_KEY);
      const data = raw ? JSON.parse(raw) : null;
      return data && typeof data === 'object' ? data : null;
    } catch (error) {
      return null;
    }
  };

  const writeDraft = (data) => {
    try {
      window.sessionStorage.setItem(VIRAL_WITHDRAWAL_KEY, JSON.stringify(data));
    } catch (error) {
      // storage blocked: no courtesy copy and no restore, the form itself is unaffected
    }
  };

  const clearDraft = () => {
    try {
      window.sessionStorage.removeItem(VIRAL_WITHDRAWAL_KEY);
    } catch (error) {
      // nothing to clean
    }
  };

  const pad = (value) => String(value).padStart(2, '0');

  /* 2026-09-19T18:42:07+02:00 : unambiguous and language-neutral, for the e-mail the merchant receives */
  const isoWithOffset = (date) => {
    const offset = -date.getTimezoneOffset();
    const sign = offset < 0 ? '-' : '+';
    const minutes = Math.abs(offset);
    const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    return `${day}T${time}${sign}${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
  };

  customElements.define(
    'viral-withdrawal',
    class ViralWithdrawal extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

        if (this.querySelector('[data-success]')) {
          this.showReceipt();
          return;
        }

        this.steps = {
          1: this.querySelector('[data-step="1"]'),
          2: this.querySelector('[data-step="2"]'),
        };
        this.nextRow = this.querySelector('[data-next-row]');
        this.nextButton = this.querySelector('[data-next]');
        this.backButton = this.querySelector('[data-back]');
        this.confirmButton = this.querySelector('[data-confirm]');
        this.review = this.querySelector('[data-review]');
        this.summary = this.querySelector('[data-client-errors]');
        this.summaryList = this.querySelector('[data-client-errors-list]');
        this.serverErrors = this.querySelector('[data-server-errors]');
        this.statement = this.querySelector('[data-statement]');

        this.fields = {};
        VIRAL_WITHDRAWAL_FIELDS.forEach((key) => {
          this.fields[key] = this.querySelector(`[data-field="${key}"]`);
        });

        const ready =
          this.steps[1] &&
          this.steps[2] &&
          this.nextRow &&
          this.nextButton &&
          this.backButton &&
          this.confirmButton &&
          this.review &&
          this.summary &&
          this.summaryList &&
          this.fields.name &&
          this.fields.order &&
          this.fields.email;
        // anything missing: leave the no-JavaScript version exactly as it is
        if (!ready) return;

        this.enhance();
      }

      disconnectedCallback() {
        if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      }

      /* ---------------------------------------------------------------- form screen */

      enhance() {
        this.requiredFields = [this.fields.name, this.fields.order, this.fields.email];
        this.requiredFields.forEach((field) => {
          field.dataset.describedby = field.getAttribute('aria-describedby') || '';
          field.addEventListener('input', () => {
            if (field.getAttribute('aria-invalid') === 'true') this.setFieldError(field, false);
          });
        });

        // The script validates (accessible summary, translated messages); step 2 replaces the checkbox.
        this.form.noValidate = true;
        const checkRow = this.querySelector('[data-confirm-check]');
        if (checkRow) {
          checkRow.hidden = true;
          checkRow.querySelectorAll('input').forEach((input) => {
            input.disabled = true;
          });
        }

        this.nextRow.hidden = false;
        this.backButton.hidden = false;
        this.review.hidden = false;
        this.setAttribute('data-enhanced', '');

        this.forgetPending();
        const restored = this.restoreDraft();
        const refillNote = this.querySelector('[data-refill-note]');
        if (restored && refillNote) refillNote.hidden = true;

        this.showStep(1, false);
        if (this.serverErrors) this.serverErrors.focus();

        this.nextButton.addEventListener('click', () => this.goToReview());
        this.backButton.addEventListener('click', () => this.showStep(1, true));
        // click fires before the native submit and is never cancelled here
        this.confirmButton.addEventListener('click', () => this.onConfirm());

        this.steps[1].addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' || event.isComposing) return;
          const target = event.target;
          if (!(target instanceof HTMLInputElement) || target.type === 'hidden') return;
          event.preventDefault();
          this.goToReview();
        });

        this.form.addEventListener(
          'submit',
          (event) => {
            const now = Date.now();
            const tooEarly = this.current !== 2;
            const repeated = this.lastSubmit && now - this.lastSubmit < VIRAL_WITHDRAWAL_RESUBMIT_MS;
            if (tooEarly || repeated || this.designMode) {
              // safety net only: on step 1 the submit button is disabled, so this should never run there
              event.preventDefault();
              event.stopImmediatePropagation();
              if (tooEarly) this.goToReview();
              return;
            }
            this.lastSubmit = now;
          },
          true
        );

        // Back/forward cache: come back to a clean step 1 and never keep a "pending" flag alive.
        this.onPageShow = (event) => {
          if (!event.persisted) return;
          this.lastSubmit = 0;
          this.forgetPending();
          this.showStep(1, false);
        };
        window.addEventListener('pageshow', this.onPageShow);
      }

      showStep(number, moveFocus) {
        this.current = number;
        this.steps[1].hidden = number !== 1;
        this.steps[2].hidden = number !== 2;
        this.confirmButton.disabled = number !== 2;
        if (!moveFocus) return;
        const heading = this.steps[number].querySelector('[data-step-heading]');
        if (heading) heading.focus();
      }

      goToReview() {
        const problems = this.validate();
        this.renderSummary(problems);
        if (problems.length) return;

        this.fillSummary(this.steps[2], this.collect());
        if (this.serverErrors) this.serverErrors.hidden = true;
        this.saveDraft(false);
        this.showStep(2, true);
      }

      onConfirm() {
        if (this.current !== 2 || this.designMode) return;
        const now = new Date();

        let stamp = this.form.querySelector('[data-device-time]');
        if (!stamp) {
          stamp = document.createElement('input');
          stamp.type = 'hidden';
          stamp.name = 'contact[hora_segun_dispositivo]';
          stamp.setAttribute('data-device-time', '');
          this.form.appendChild(stamp);
        }
        stamp.value = isoWithOffset(now);

        this.saveDraft(true, now);
      }

      collect() {
        const data = {};
        VIRAL_WITHDRAWAL_FIELDS.forEach((key) => {
          const field = this.fields[key];
          data[key] = field ? field.value.trim() : '';
        });
        data.statement = this.statement ? this.statement.textContent.trim() : '';
        return data;
      }

      validate() {
        const problems = [];
        this.requiredFields.forEach((field) => {
          let invalid = field.value.trim() === '';
          if (!invalid && field.type === 'email') invalid = !field.checkValidity();
          this.setFieldError(field, invalid);
          if (invalid) problems.push(field);
        });
        return problems;
      }

      setFieldError(field, invalid) {
        const message = this.querySelector(`[data-error-for="${field.dataset.field}"]`);
        const described = field.dataset.describedby || '';

        if (invalid) {
          field.setAttribute('aria-invalid', 'true');
          if (message) {
            message.textContent = field.dataset.errorMessage || '';
            message.hidden = false;
            field.setAttribute('aria-describedby', `${message.id} ${described}`.trim());
          }
          return;
        }

        field.removeAttribute('aria-invalid');
        if (message) {
          message.hidden = true;
          message.textContent = '';
        }
        if (described) {
          field.setAttribute('aria-describedby', described);
        } else {
          field.removeAttribute('aria-describedby');
        }
      }

      renderSummary(problems) {
        this.summaryList.textContent = '';
        problems.forEach((field) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.className = 'link';
          link.href = `#${field.id}`;
          link.textContent = field.dataset.errorMessage || '';
          link.addEventListener('click', (event) => {
            event.preventDefault();
            field.focus();
          });
          item.appendChild(link);
          this.summaryList.appendChild(item);
        });

        this.summary.hidden = problems.length === 0;
        if (problems.length) this.summary.focus();
      }

      fillSummary(root, data) {
        root.querySelectorAll('[data-summary]').forEach((node) => {
          const key = node.dataset.summary;
          let value = typeof data[key] === 'string' ? data[key] : '';
          if (!value && key === 'items') value = this.dataset.wholeOrder || '';
          if (!value && key === 'reason') value = this.dataset.noReason || '';
          node.textContent = value;
        });
      }

      /* ---------------------------------------------------------------- draft in sessionStorage */

      saveDraft(pending, date) {
        const data = this.collect();
        data.pending = Boolean(pending);
        if (date) data.at = date.toISOString();
        writeDraft(data);
      }

      /* A submit that never reached the confirmation screen must not be shown as received later on. */
      forgetPending() {
        const draft = readDraft();
        if (!draft || !draft.pending) return;
        draft.pending = false;
        delete draft.at;
        writeDraft(draft);
      }

      /* Only fills fields that are empty: never overwrites what Shopify or the customer account provided. */
      restoreDraft() {
        const draft = readDraft();
        if (!draft) return false;
        let restored = false;
        VIRAL_WITHDRAWAL_FIELDS.forEach((key) => {
          const field = this.fields[key];
          if (!field || field.value || typeof draft[key] !== 'string' || !draft[key]) return;
          field.value = draft[key];
          restored = true;
        });
        return restored;
      }

      /* ---------------------------------------------------------------- confirmation screen */

      showReceipt() {
        const receipt = this.querySelector('[data-receipt]');
        const draft = readDraft();

        if (receipt && draft && draft.pending) {
          const data = Object.assign({}, draft, { time: this.formatTime(draft.at) });
          this.fillSummary(receipt, data);
          receipt.hidden = false;
        }
        if (!this.designMode) clearDraft();

        const printButton = this.querySelector('[data-print]');
        if (printButton && typeof window.print === 'function') {
          printButton.hidden = false;
          printButton.addEventListener('click', () => window.print());
        }

        this.cleanAddress();

        const heading = this.querySelector('[data-success-heading]');
        if (heading) heading.focus();
      }

      formatTime(iso) {
        if (!iso) return '';
        try {
          const date = new Date(iso);
          if (Number.isNaN(date.getTime())) return '';
          const options = { dateStyle: 'full', timeStyle: 'long' };
          return new Intl.DateTimeFormat(this.dataset.locale || undefined, options).format(date);
        } catch (error) {
          return iso;
        }
      }

      cleanAddress() {
        if (this.designMode || !window.history || typeof window.history.replaceState !== 'function') return;
        try {
          const url = new URL(window.location.href);
          if (!url.searchParams.has('contact_posted')) return;
          url.searchParams.delete('contact_posted');
          url.hash = '';
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (error) {
          // the address stays as it is, the hint on screen still explains it
        }
      }
    }
  );
}
